import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Button from '@/components-latest/primitives/Button'
import Checkbox from '@/components-latest/primitives/Controls/Checkbox'
import Label from '@/components-latest/primitives/Label'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { selectAppSettings } from '@/ducks/app/selectors/app.selectors'
import { updateSettings } from '@/ducks/app/slices/app.slice'

import Styles from './HideMenuModal.module.scss'

interface IHideMenuModal {
  onConfirm: () => void
  children: React.ReactNode
  name?: string
}

const HideMenuModal: React.FC<IHideMenuModal> = ({
  onConfirm,
  children,
  name = ''
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const settings = useSelector(selectAppSettings)
  const shouldShowConfirm = useRef(settings.confirmMenuHideAction)

  const [open, setOpen] = useState(false)

  const closeModal = () => setOpen(false)
  const handleConfirm = () => {
    onConfirm()
    dispatch(
      updateSettings({ confirmMenuHideAction: shouldShowConfirm.current })
    )
    closeModal()
  }

  const footer = (
    <div className={Styles.footer}>
      <Button
        size="medium"
        variant="tertiary"
        type="button"
        onClick={closeModal}>
        {t('common.modal.cancel')}
      </Button>
      <Button
        size="medium"
        variant="primary"
        type="button"
        onClick={handleConfirm}>
        {t('common.modal.yes')}
      </Button>
    </div>
  )

  const body = (
    <div className={Styles.body}>
      <Text variant="nextSubtitle" gutter={8}>
        {t('menu.hide-modal.description', { name })}
      </Text>
      <Label className={Styles.checkboxRow}>
        <Checkbox
          defaultChecked={!shouldShowConfirm.current}
          onCheckedChange={checked => (shouldShowConfirm.current = !checked)}
        />
        {t('menu.hide-modal.setting.label')}
      </Label>
    </div>
  )

  return (
    <CommonModal
      width={376}
      open={open}
      onOpenChange={(state: boolean) => setOpen(state)}
      title={t('menu.hide-modal.title', { name })}
      footer={footer}
      body={body}>
      {children}
    </CommonModal>
  )
}

export default HideMenuModal
